import { useEffect, useMemo, useState } from 'react';
import { Doughnut, Line, Bar } from 'react-chartjs-2';
import {
  FaCalendarCheck,
  FaHourglassHalf,
  FaThumbsUp,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaUserMd,
  FaPercent,
  FaStar,
} from 'react-icons/fa';
import analyticsApi from '../../../services/analyticsApi';
import {
  StatCard,
  ChartCard,
  STATUS_COLORS,
  statusDoughnutData,
  doughnutOptions,
  baseBarOptions,
  lineOptions,
} from '../analytics/analyticsShared';

// Admin booking & specialist analytics dashboard.
function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [specialistSort, setSpecialistSort] = useState('total');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await analyticsApi.getAdminBookings();
        if (mounted) setData(res.analytics);
      } catch (err) {
        /* interceptor toasts */
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const sortedSpecialists = useMemo(() => {
    if (!data?.perSpecialist) return [];
    const arr = [...data.perSpecialist];
    arr.sort((a, b) => (b[specialistSort] || 0) - (a[specialistSort] || 0));
    return arr;
  }, [data, specialistSort]);

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-500">Loading analytics…</div>;
  }

  if (!data) {
    return <div className="text-center text-gray-500 mt-10">No analytics data available.</div>;
  }

  const { status, type, monthly, hourOfDay, dayOfWeek } = data;

  const monthlyData = {
    labels: monthly.map((m) => m.label),
    datasets: [
      {
        data: monthly.map((m) => m.count),
        borderColor: '#1e5c33',
        backgroundColor: 'rgba(30,92,51,0.12)',
        borderWidth: 2,
        tension: 0.35,
        fill: true,
        pointRadius: 3,
      },
    ],
  };

  const hourData = {
    labels: hourOfDay.map((h) => `${String(h.hour).padStart(2, '0')}`),
    datasets: [
      {
        data: hourOfDay.map((h) => h.count),
        backgroundColor: '#7caf64',
        borderRadius: 3,
      },
    ],
  };

  const dowData = {
    labels: dayOfWeek.map((d) => d.label),
    datasets: [
      {
        data: dayOfWeek.map((d) => d.count),
        backgroundColor: '#1e5c33',
        borderRadius: 3,
      },
    ],
  };

  const typeData = {
    labels: ['Remote', 'On-site'],
    datasets: [
      {
        data: [type.remote || 0, type['on-site'] || 0],
        backgroundColor: ['#7caf64', '#1e5c33'],
        borderWidth: 0,
      },
    ],
  };

  return (
    <div className="p-1 sm:p-3 space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-primary">Booking &amp; Specialist Analytics</h1>
        <p className="text-sm text-gray-500">Platform-wide consultations, specialist performance &amp; booking patterns</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <StatCard label="Total Bookings" value={data.total} icon={<FaCalendarCheck />} />
        <StatCard label="Pending" value={status.pending} icon={<FaHourglassHalf />} accent="text-amber-600" />
        <StatCard label="Accepted" value={status.accepted} icon={<FaThumbsUp />} accent="text-blue-600" />
        <StatCard label="Completed" value={status.completed} icon={<FaCheckCircle />} accent="text-primary" />
        <StatCard label="Declined" value={status.declined} icon={<FaTimesCircle />} accent="text-red-600" />
        <StatCard label="Upcoming" value={data.upcoming} icon={<FaClock />} accent="text-secondary" />
        <StatCard label="Specialists" value={data.activeSpecialists} icon={<FaUserMd />} accent="text-primary" />
        <StatCard label="Completion" value={`${data.completionRate}%`} icon={<FaPercent />} accent="text-primary" />
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ChartCard title="Status Breakdown">
          <div style={{ height: 250 }}>
            <Doughnut data={statusDoughnutData(status)} options={doughnutOptions} />
          </div>
        </ChartCard>
        <ChartCard title="Bookings Over Time (6 months)" className="lg:col-span-2">
          <div style={{ height: 250 }}>
            <Line data={monthlyData} options={lineOptions} />
          </div>
        </ChartCard>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ChartCard title="Time of Booking (hour of day)" className="lg:col-span-2">
          <div style={{ height: 220 }}>
            <Bar data={hourData} options={baseBarOptions} />
          </div>
        </ChartCard>
        <ChartCard title="Consultation Type">
          <div style={{ height: 220 }}>
            <Doughnut data={typeData} options={doughnutOptions} />
          </div>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ChartCard title="Bookings by Day of Week">
          <div style={{ height: 220 }}>
            <Bar data={dowData} options={baseBarOptions} />
          </div>
        </ChartCard>
        <div className="lg:col-span-2" />
      </div>

      {/* Per-specialist performance table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Specialist Performance</h3>
          <select
            value={specialistSort}
            onChange={(e) => setSpecialistSort(e.target.value)}
            className="text-xs border border-gray-200 rounded px-2 py-1 text-gray-600"
          >
            <option value="total">Sort: Total bookings</option>
            <option value="completed">Sort: Completed</option>
            <option value="pending">Sort: Pending</option>
            <option value="completionRate">Sort: Completion rate</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Specialist</th>
                <th className="text-left px-3 py-2 font-medium">Rating</th>
                <th className="text-left px-3 py-2 font-medium">Total</th>
                <th className="text-left px-3 py-2 font-medium">Pending</th>
                <th className="text-left px-3 py-2 font-medium">Accepted</th>
                <th className="text-left px-3 py-2 font-medium">Completed</th>
                <th className="text-left px-3 py-2 font-medium">Declined</th>
                <th className="text-left px-3 py-2 font-medium">Canceled</th>
                <th className="text-left px-3 py-2 font-medium">Completion</th>
                <th className="text-left px-3 py-2 font-medium">Last Booking</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedSpecialists.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-6 text-center text-gray-400">No specialist bookings yet.</td>
                </tr>
              )}
              {sortedSpecialists.map((s) => (
                <tr key={s.specialistId} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-800">
                    <div className="font-medium">{s.name || 'Unknown'}</div>
                    <div className="text-xs text-gray-400">{s.specialty || s.email || ''}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1 text-amber-500">
                      <FaStar className="text-xs" />
                      <span className="text-gray-700">{s.rating != null ? Number(s.rating).toFixed(1) : '—'}</span>
                    </span>
                  </td>
                  <td className="px-3 py-2 font-semibold text-gray-800">{s.total}</td>
                  <td className="px-3 py-2" style={{ color: STATUS_COLORS.pending }}>{s.pending}</td>
                  <td className="px-3 py-2" style={{ color: STATUS_COLORS.accepted }}>{s.accepted}</td>
                  <td className="px-3 py-2" style={{ color: STATUS_COLORS.completed }}>{s.completed}</td>
                  <td className="px-3 py-2" style={{ color: STATUS_COLORS.declined }}>{s.declined}</td>
                  <td className="px-3 py-2" style={{ color: STATUS_COLORS.canceled }}>{s.canceled}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-14 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${s.completionRate}%` }} />
                      </div>
                      <span className="text-xs text-gray-600">{s.completionRate}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {s.lastBooking ? new Date(s.lastBooking).toLocaleDateString('en-GB') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Analytics;
